// CHSP: the Commonwealth Home Support Programme.
//
// CHSP is grant-funded, not Support at Home: there is no budget to draw down and nothing is claimed
// from Services Australia. The provider agrees output targets with the Department (units of each
// service a year, at a unit price), may ask each participant for a contribution under its own
// policy (reduced or waived for hardship), and reports what it delivered to DEX every month by the
// 14th (CHSP Manual §13.5). So this screen is three things:
//   Overview      delivered against target, the DEX report due, wellness and reablement periods
//   Services      which service types are CHSP services, the contribution, and the year's target
//   Participants  who is enrolled, for which services, and any reduction of their contribution
// The rules live in backend/utils/chsp.js.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import api from '../../utils/api';
import { isoDate } from '../../utils/time';
import toast from 'react-hot-toast';
import { PersonAvatar, Tabs, useUrlTab } from '../../components/ui';
import { useConfirm } from '../../components/ConfirmDialog';
import ChspMoney, { ChspPolicies } from './ChspMoney';
import {
  HeartHandshake, Target, Users, UserPlus, Save, FileBarChart2, Sprout, Settings2, AlertTriangle,
} from 'lucide-react';
import {
  PageHead, Panel, Register, FigureStrip, Figure, Field, FormRow, DetailDrawer, Deadline, RailList, RailRow,
  Currency, DataTable, ErrorState, fmtDate, money, monthLabel,
} from './design';

const UNIT = { hour: 'Hours', each: 'Each', meal: 'Meals', trip: 'Trips', session: 'Sessions' };
const unitWord = (u, n) => {
  const w = { hour: ['hour', 'hours'], each: ['unit', 'units'], meal: ['meal', 'meals'], trip: ['trip', 'trips'], session: ['session', 'sessions'] }[u || 'hour'] || ['unit', 'units'];
  return Number(n) === 1 ? w[0] : w[1];
};
const num = (v) => (v == null ? '—' : Number(v).toLocaleString('en-AU', { maximumFractionDigits: 2 }));
const STATUS = {
  on_track: { word: 'On track', cls: 'text-slate-700', bar: 'hue-bar' },
  ahead: { word: 'Ahead', cls: 'text-teal-800', bar: 'hue-bar' },
  behind: { word: 'Behind', cls: 'text-amber-800', bar: 'bg-amber-500' },
  no_target: { word: 'No target', cls: 'text-slate-500', bar: 'bg-slate-300' },
};
const EXIT_REASONS = [
  'Moved to Support at Home', 'Moved to residential aged care', 'No longer needs services', 'Moved out of the area',
  'Chose another provider', 'Deceased', 'Other',
];
const REABLEMENT = { active: ['Running', 'badge-green'], ending_soon: ['Ends soon', 'badge-yellow'], ended: ['Ended', 'badge-gray'] };
// The day before a YYYY-MM-DD date: CHSP ends the day before Support at Home starts.
const dayBefore = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) - 1)).toISOString().slice(0, 10) : null;
};

// The financial years to choose from: this one and the two before it.
function fyOptions() {
  const t = new Date();
  const start = t.getMonth() >= 6 ? t.getFullYear() : t.getFullYear() - 1;
  return [0, 1, 2].map((i) => { const y = start - i; return `${y}-${String(y + 1).slice(2)}`; });
}

// Delivered so far, with a mark where the year's pace says it should be by now.
function ProgressBar({ pct, expectedPct, status }) {
  const st = STATUS[status] || STATUS.no_target;
  const fill = Math.min(100, Math.max(0, Number(pct) || 0));
  return (
    <span className="block min-w-0">
      <span className="flex items-baseline justify-between gap-2 text-[12px]">
        <span className={clsx('font-medium', st.cls)}>{st.word}</span>
        <span className="tabular-nums text-slate-500">{pct == null ? '' : `${num(pct)}%`}</span>
      </span>
      <span className="relative mt-1 block h-1.5 rounded-full bg-slate-100">
        <span className="absolute inset-0 overflow-hidden rounded-full">
          <span className={clsx('block h-full rounded-full', st.bar)} style={{ width: `${fill}%` }} />
        </span>
        {status !== 'no_target' && (
          <span className="absolute -top-0.5 -bottom-0.5 w-0.5 rounded bg-slate-700/70" style={{ left: `calc(${Math.min(100, expectedPct || 0)}% - 1px)` }}
            title={`${num(expectedPct)}% of the year has gone`} aria-hidden="true" />
        )}
      </span>
    </span>
  );
}

export default function AgedCareChsp({ onGoTo }) {
  const [view, setView] = useUrlTab('view', 'overview', ['overview', 'services', 'participants']);
  const years = useMemo(fyOptions, []);
  const [fy, setFy] = useState(years[0]);
  const [reference, setReference] = useState({ services: [], units: Object.keys(UNIT) });
  const [referenceError, setReferenceError] = useState(null);
  // Without it the CHSP service type list is empty; the set-up drawer says so and offers a retry
  // rather than leaving an empty list that looks like there is nothing to choose.
  const loadReference = useCallback(() => {
    setReferenceError(null);
    api.get('/aged-care/chsp/reference').then((r) => setReference(r.data || {}))
      .catch((e) => setReferenceError(e.response?.data?.message || 'Could not load the CHSP service types'));
  }, []);
  useEffect(() => { loadReference(); }, [loadReference]);

  return (
    <div>
      <PageHead icon={HeartHandshake}
        title="CHSP"
        purpose="The Commonwealth Home Support Programme. It is grant-funded: nothing is claimed and there is no budget. Participants pay a contribution under your fee policy, and what you deliver is reported to DEX every month, by the 14th."
        actions={(
          <label className="flex items-center gap-2 text-[13px] text-slate-600">
            <span>Financial year</span>
            <select className="input-field !w-auto !py-1.5" value={fy} onChange={(e) => setFy(e.target.value)} aria-label="Financial year">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
        )}
      />
      <Tabs className="mb-5" value={view} onChange={setView} ariaLabel="CHSP sections"
        tabs={[
          { id: 'overview', label: 'Overview', icon: Target },
          { id: 'services', label: 'Services and targets', icon: Settings2 },
          { id: 'participants', label: 'Participants', icon: Users },
        ]} />
      {view === 'services' && <><ChspServices fy={fy} reference={reference} referenceError={referenceError} onRetryReference={loadReference} /><ChspPolicies /></>}
      {view === 'participants' && <ChspParticipants />}
      {view === 'overview' && <><ChspOverview fy={fy} onGoTo={onGoTo} onView={setView} /><ChspMoney fy={fy} /></>}
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────────────────────
function ChspOverview({ fy, onGoTo, onView }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await api.get(`/aged-care/chsp/overview?fy=${encodeURIComponent(fy)}`); setData(r.data); }
    catch (e) { setError(e.response?.data?.message || 'Could not load the CHSP overview'); }
    finally { setLoading(false); }
  }, [fy]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const rep = data?.reporting;
  const services = data?.services || [];
  const columns = [
    {
      key: 'name', header: 'Service', sortable: true,
      render: (r) => (
        <span className="min-w-0 block">
          <span className="font-medium text-slate-900 block truncate">{r.name}</span>
          <span className="text-[12px] text-slate-500">{r.chspService || 'CHSP service type not chosen'}</span>
        </span>
      ),
    },
    {
      key: 'delivered', header: 'Delivered', width: 140, align: 'right', sortable: true,
      render: (r) => <span className="tabular-nums text-slate-800">{num(r.delivered)} <span className="text-slate-500 text-[12px]">{unitWord(r.unit, r.delivered)}</span></span>,
    },
    {
      key: 'target', header: 'Target', width: 120, align: 'right', sortable: true,
      render: (r) => (r.target > 0 ? <span className="tabular-nums text-slate-600">{num(r.target)}</span> : <span className="text-slate-500">Not set</span>),
    },
    {
      key: 'pct', header: 'Against the year', width: 200, sortable: true, sortValue: (r) => (r.pct == null ? -1 : r.pct),
      render: (r) => <ProgressBar pct={r.pct} expectedPct={r.expectedPct} status={r.status} />,
    },
    { key: 'deliveredValue', header: 'Value delivered', width: 140, align: 'right', money: true, sortable: true, render: (r) => <Currency value={r.deliveredValue} /> },
    { key: 'grant', header: 'Grant', width: 130, align: 'right', money: true, sortable: true, render: (r) => (r.grant > 0 ? <Currency value={r.grant} /> : <span className="text-slate-500">—</span>) },
    { key: 'fees', header: 'Contributions', width: 130, align: 'right', money: true, sortable: true, render: (r) => <Currency value={r.fees} /> },
  ];

  return (
    <div>
      <FigureStrip>
        <Figure label="Enrolled now" value={t.clientsActive ?? '—'} icon={Users} />
        <Figure label="Served this year" value={t.clientsServed ?? '—'} sub={t.visits != null ? `${t.visits} visit${t.visits === 1 ? '' : 's'} delivered` : null} />
        <Figure label="Value delivered" value={money(t.deliveredValue || 0)} tone="teal" sub={t.grant > 0 ? `of ${money(t.grant)} in targets` : 'Set targets and unit prices'} />
        <Figure label="Contributions billed" value={money(t.fees || 0)} sub="Invoiced to participants" />
      </FigureStrip>

      {/* A failed load shows only the failure: the target table and reablement list would
          otherwise say "no CHSP services yet" and "no periods", which is not what happened. */}
      {error && (
        <div className="rounded-xl border border-slate-200 bg-white mb-5">
          <ErrorState message={error} onRetry={load} />
        </div>
      )}

      {!error && <>
      <RailList className="mb-5">
        {rep && (
          <RailRow
            severity={rep.reportedOn ? 'done' : rep.overdue ? 'overdue' : rep.daysLeft <= 7 ? 'soon' : 'open'}
            title={`${rep.monthLabel} report to DEX`}
            detail={rep.reportedOn
              ? (rep.reportedBy === 'web services'
                ? `Sent to DEX by web services on ${fmtDate(rep.reportedOn)}. Any refused records are in the request log on the DEX screen.`
                : `A DEX file covering ${rep.monthLabel} was built on ${fmtDate(rep.reportedOn)}. Check it was uploaded in the DEX portal.`)
              : `Build the file on the DEX screen and upload it in the DEX portal by the 14th.`}
            right={rep.reportedOn ? <span className="text-emerald-700 font-medium">{rep.reportedBy === 'web services' ? 'Sent' : 'Built'}</span> : <Deadline date={rep.due} />}
            onClick={() => onGoTo?.('dex')}
          />
        )}
        {(data?.unsetServices || []).map((u) => (
          <RailRow key={u.name} severity="overdue"
            title={`${u.n} CHSP visit${u.n === 1 ? '' : 's'} booked as ${u.name}`}
            detail="That service is not set up as a CHSP service, so these visits are held, not counted and not reported. Set it up under Services and targets."
            onClick={() => onView('services')} />
        ))}
      </RailList>

      <Panel title={`Delivered against target, ${data?.fy?.label || fy}`} icon={Target} className="mb-5"
        actions={<button type="button" className="btn-secondary btn-sm" onClick={() => onView('services')}><Settings2 size={14} /> Targets</button>}>
        <DataTable
          columns={columns} rows={services} rowKey="serviceTypeId" loading={loading} label="CHSP services"
          onRowClick={(r) => setOpen(r)} stickyHeader={false}
          empty={{
            icon: Target, title: 'No CHSP services yet',
            hint: 'Choose which of your service types are CHSP services, the contribution for each, and the year\'s target from your grant agreement.',
            action: <button type="button" className="btn-primary btn-sm" onClick={() => onView('services')}><Settings2 size={14} /> Set up services</button>,
          }}
          footer={services.length ? 'Hours are counted to the nearest quarter hour from the clocked time; other services count one per visit. The mark on each bar is how much of the year has gone.' : null}
        />
      </Panel>

      <Panel title="Wellness and reablement" icon={Sprout}>
        {(data?.reablement || []).length ? (
          <div className="divide-y divide-slate-100">
            {data.reablement.map((p) => {
              // Once the outcome is recorded the period is finished, whatever its end date says.
              const [word, cls] = p.outcome ? ['Finished', 'badge-gray'] : REABLEMENT[p.status] || ['—', 'badge-gray'];
              return (
                <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <span className="flex items-center gap-2.5 min-w-0">
                    <PersonAvatar id={p.client_id} name={p.name} size="sm" />
                    <span className="min-w-0">
                      <span className="block font-medium text-slate-900 truncate">{p.name}</span>
                      <span className="block text-[12px] text-slate-500 truncate">{p.title || 'Reablement period'}{p.outcome ? `, outcome recorded` : ''}</span>
                    </span>
                  </span>
                  <span className="flex items-center gap-3 text-[13px]">
                    {p.reablement_end ? <Deadline date={p.reablement_end} done={!!p.outcome || p.status === 'ended'} /> : <span className="text-slate-500">No end date</span>}
                    <span className={clsx('badge', cls)}>{word}</span>
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="px-4 py-4 text-[13px] text-slate-500">
            No wellness and reablement periods. A care plan can be set as a time-limited reablement period in Care plans, with goals and an outcome at the end.
            <button type="button" className="ml-1 font-medium text-primary-700 hover:underline" onClick={() => onGoTo?.('careplans')}>Open care plans</button>
          </p>
        )}
      </Panel>
      </>}

      {open && (
        <DetailDrawer open onClose={() => setOpen(null)} icon={Target} width="sm" title={open.name}
          subtitle={`${open.chspService || 'CHSP service'}, ${data?.fy?.label || fy}`}>
          <div className="space-y-4">
            <FormRow cols={2}>
              <Figure label="Delivered" value={num(open.delivered)} sub={UNIT[open.unit] || 'Units'} />
              <Figure label="Target" value={open.target > 0 ? num(open.target) : 'Not set'} />
              <Figure label="Visits" value={open.visits} />
              <Figure label="Contributions" value={money(open.fees || 0)} />
            </FormRow>
            <div>
              <h4 className="text-[13px] font-semibold text-slate-800 mb-2">By month</h4>
              <MonthBars rows={open.byMonth || []} unit={open.unit} target={open.target} />
            </div>
          </div>
        </DetailDrawer>
      )}
    </div>
  );
}

// Units delivered each month of the year, against a twelfth of the target.
function MonthBars({ rows, unit, target }) {
  const monthly = target > 0 ? target / 12 : 0;
  const max = Math.max(monthly, ...rows.map((r) => r.units), 1);
  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.month} className="grid grid-cols-[88px_1fr_64px] items-center gap-2 text-[12px]">
          <span className="text-slate-600">{monthLabel(r.month)}</span>
          <span className="relative h-2 rounded-full bg-slate-100">
            <span className="absolute inset-y-0 left-0 rounded-full hue-bar" style={{ width: `${(r.units / max) * 100}%` }} />
            {monthly > 0 && <span className="absolute -top-0.5 -bottom-0.5 w-0.5 rounded bg-slate-700/60" style={{ left: `calc(${(monthly / max) * 100}% - 1px)` }} aria-hidden="true" />}
          </span>
          <span className="text-right tabular-nums text-slate-700">{num(r.units)} <span className="text-slate-500">{unitWord(unit, r.units)}</span></span>
        </div>
      ))}
      {monthly > 0 && <p className="text-[11px] text-slate-500 pt-1">The mark is a twelfth of the year's target.</p>}
    </div>
  );
}

// ── Services and targets ──────────────────────────────────────────────────────────────────────
function ChspServices({ fy, reference, referenceError, onRetryReference }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await api.get(`/aged-care/chsp/services?fy=${encodeURIComponent(fy)}`); setRows(r.data?.services || []); }
    catch (e) { setError(e.response?.data?.message || 'Could not load your services'); setRows([]); }
    finally { setLoading(false); }
  }, [fy]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => `${r.name} ${r.chsp_service || ''}`.toLowerCase().includes(q)) : rows;
  }, [rows, search]);
  const openEdit = (r) => setEdit({
    // A service never set up opens ready to set up; one switched off stays off until ticked.
    id: r.service_type_id, name: r.name, isChsp: !!r.is_chsp || !r.chsp_service,
    wasChsp: !!r.is_chsp, chspService: r.chsp_service || '', unit: r.unit || 'hour',
    feePerUnit: r.fee_per_unit ?? '', targetUnits: r.target_units ?? '', unitCost: r.unit_cost ?? '',
  });
  const save = async () => {
    if (edit.isChsp && (edit.feePerUnit === '' || Number(edit.feePerUnit) < 0)) { toast.error('Enter the contribution per unit (0 for a free service)'); return; }
    setSaving(true);
    try {
      const saved = await api.put(`/aged-care/chsp/services/${edit.id}`, edit.isChsp
        ? { chspService: edit.chspService || null, unit: edit.unit, feePerUnit: Number(edit.feePerUnit) || 0, fy, targetUnits: Number(edit.targetUnits) || 0, unitCost: Number(edit.unitCost) || 0 }
        : { isChsp: false });
      toast.success(edit.isChsp ? `${edit.name} is a CHSP service` : `${edit.name} is no longer a CHSP service`);
      if (saved.data?.warning) toast(saved.data.warning, { icon: '⚠️', duration: 8000 });
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
    finally { setSaving(false); }
  };

  const setUp = rows.filter((r) => r.is_chsp);
  const columns = [
    {
      key: 'name', header: 'Service type', sortable: true,
      render: (r) => (
        <span className="min-w-0 block">
          <span className="font-medium text-slate-900 block truncate">{r.name}</span>
          <span className={clsx('text-[12px]', r.is_chsp ? 'text-teal-800' : 'text-slate-500')}>{r.is_chsp ? (r.chsp_service || 'CHSP service') : 'Not a CHSP service'}</span>
          {r.contributionWarning && <span className="block text-[12px] text-amber-700">{r.contributionWarning}</span>}
        </span>
      ),
    },
    { key: 'unit', header: 'Counted in', width: 110, render: (r) => (r.is_chsp ? UNIT[r.unit] || 'Hours' : '—') },
    { key: 'fee_per_unit', header: 'Contribution', width: 130, align: 'right', money: true, render: (r) => (r.is_chsp ? (Number(r.fee_per_unit) > 0 ? <Currency value={r.fee_per_unit} /> : <span className="text-slate-600">Free</span>) : '—') },
    { key: 'target_units', header: `Target ${fy}`, width: 120, align: 'right', render: (r) => (r.is_chsp ? (Number(r.target_units) > 0 ? <span className="tabular-nums">{num(r.target_units)}</span> : <span className="text-amber-700">Not set</span>) : '—') },
    { key: 'unit_cost', header: 'Unit price', width: 110, align: 'right', money: true, render: (r) => (r.is_chsp && Number(r.unit_cost) > 0 ? <Currency value={r.unit_cost} /> : '—') },
    {
      key: 'dex_mapped', header: 'DEX', width: 120,
      render: (r) => (!r.is_chsp ? '—' : r.dex_mapped ? <span className="text-emerald-700">Mapped</span> : <span className="text-amber-700">Map on DEX screen</span>),
    },
  ];

  return (
    <div>
      <FigureStrip>
        <Figure label="CHSP services" value={setUp.length} icon={Settings2} />
        <Figure label="With a target this year" value={setUp.filter((r) => Number(r.target_units) > 0).length} tone={setUp.some((r) => !(Number(r.target_units) > 0)) ? 'amber' : 'slate'} />
        <Figure label="Mapped for DEX" value={setUp.filter((r) => r.dex_mapped).length} tone={setUp.some((r) => !r.dex_mapped) ? 'amber' : 'slate'} sub={setUp.some((r) => !r.dex_mapped) ? 'Unmapped visits are left out of the DEX file' : null} />
      </FigureStrip>
      <Register
        title="Services" count={filtered.length} countLabel="service types" icon={Settings2}
        search={search} onSearch={setSearch} searchPlaceholder="Search services"
        columns={columns} rows={filtered} rowKey="service_type_id" loading={loading} error={error} onRetry={load}
        onRowClick={openEdit} rowTone={(r) => (r.is_chsp && !(Number(r.target_units) > 0) ? 'amber' : undefined)}
        empty={{ icon: Settings2, title: 'No service types yet', hint: 'Add your service types in Settings, Service types, then set the CHSP ones up here.' }}
        footer="A CHSP participant can only be booked for a CHSP service. The contribution is what the participant pays per unit under your fee policy; the target and unit price come from your grant agreement (target × unit price is the grant for that service)."
      />

      {edit && (
        <DetailDrawer open onClose={() => setEdit(null)} icon={Settings2} width="sm" title={edit.name} subtitle="CHSP set-up"
          footer={(
            <>
              <button type="button" onClick={() => setEdit(null)} className="btn-secondary">Cancel</button>
              <button type="button" onClick={save} disabled={saving} className="btn-primary"><Save size={15} /> {saving ? 'Saving…' : 'Save'}</button>
            </>
          )}>
          <div className="space-y-4">
            <label className="flex items-start gap-2.5 text-[13px] text-slate-800">
              <input type="checkbox" className="mt-0.5" checked={edit.isChsp} onChange={(e) => setEdit((p) => ({ ...p, isChsp: e.target.checked }))} />
              <span><span className="font-medium">This is a CHSP service</span><span className="block text-slate-500 text-[12px]">CHSP participants can be booked for it, and it is counted against your targets and reported to DEX.</span></span>
            </label>
            {edit.isChsp && (
              <>
                <Field label="CHSP service type" hint={reference.servicesSource === 'dex' ? 'The service types of your DEX activities. The DEX service type for reporting is chosen on the DEX screen.' : 'The CHSP Manual\'s older names. Once DEX is set up, the list comes from your DEX activities.'}>
                  {(id) => (
                    <select id={id} className="input-field" value={edit.chspService} onChange={(e) => setEdit((p) => ({ ...p, chspService: e.target.value }))}>
                      <option value="">Choose…</option>
                      {(reference.services || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                </Field>
                {referenceError && (
                  <p className="text-[12px] text-red-800" role="alert">
                    {referenceError} <button type="button" className="underline ml-1" onClick={onRetryReference}>Try again</button>
                  </p>
                )}
                <FormRow cols={2}>
                  <Field label="Counted in" hint="Hours for time-based services; one per visit otherwise.">
                    {(id) => (
                      <select id={id} className="input-field" value={edit.unit} onChange={(e) => setEdit((p) => ({ ...p, unit: e.target.value }))}>
                        {(reference.units || Object.keys(UNIT)).map((u) => <option key={u} value={u}>{UNIT[u] || u}</option>)}
                      </select>
                    )}
                  </Field>
                  <Field label="Contribution per unit" hint="What the participant pays. 0 for a free service.">
                    {(id) => <input id={id} type="number" min="0" step="0.01" inputMode="decimal" className="input-field" value={edit.feePerUnit} onChange={(e) => setEdit((p) => ({ ...p, feePerUnit: e.target.value }))} placeholder="0.00" />}
                  </Field>
                </FormRow>
                <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                  <p className="text-[13px] font-medium text-slate-800">Target for {fy}</p>
                  <FormRow cols={2}>
                    <Field label={`Target (${(UNIT[edit.unit] || 'units').toLowerCase()})`}>
                      {(id) => <input id={id} type="number" min="0" step="0.25" inputMode="decimal" className="input-field" value={edit.targetUnits} onChange={(e) => setEdit((p) => ({ ...p, targetUnits: e.target.value }))} placeholder="0" />}
                    </Field>
                    <Field label="Unit price" hint="The Department's funded price per unit.">
                      {(id) => <input id={id} type="number" min="0" step="0.01" inputMode="decimal" className="input-field" value={edit.unitCost} onChange={(e) => setEdit((p) => ({ ...p, unitCost: e.target.value }))} placeholder="0.00" />}
                    </Field>
                  </FormRow>
                  {Number(edit.targetUnits) > 0 && Number(edit.unitCost) > 0 && (
                    <p className="text-[12px] text-slate-600">Grant for this service: <span className="font-medium tabular-nums">{money(Number(edit.targetUnits) * Number(edit.unitCost))}</span></p>
                  )}
                </div>
              </>
            )}
            {!edit.isChsp && edit.wasChsp && (
              <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" /> CHSP participants booked for this service will be held until they are moved to a CHSP service.
              </p>
            )}
          </div>
        </DetailDrawer>
      )}
    </div>
  );
}

// ── Participants ──────────────────────────────────────────────────────────────────────────────
function ChspParticipants() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [services, setServices] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [form, setForm] = useState(null);   // { mode: 'new' | 'edit', ... }
  const [saving, setSaving] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [policiesError, setPoliciesError] = useState(null);
  const [move, setMove] = useState(null);       // { sahStart, result }
  const confirm = useConfirm();
  const loadPolicies = useCallback(() => {
    setPoliciesError(null);
    api.get('/aged-care/chsp/policies').then((r) => setPolicies(r.data || []))
      .catch((e) => { setPolicies([]); setPoliciesError(e.response?.data?.message || 'Could not load the contribution policy versions'); });
  }, []);
  useEffect(() => { loadPolicies(); }, [loadPolicies]);
  // Asks first: the move ends the CHSP enrolment, closes the DEX case with an exit reason and moves
  // later visits to Support at Home, none of which is undone by closing the drawer.
  const transition = async () => {
    const end = dayBefore(move.sahStart);
    const ok = await confirm(
      `Their CHSP enrolment ends on ${fmtDate(end)}, the day before Support at Home starts. Their DEX case closes with the exit reason for moving to Support at Home, and visits from ${fmtDate(move.sahStart)} are checked under Support at Home instead of CHSP.`,
      { title: `Move ${form.name || 'this participant'} to Support at Home?`, confirmLabel: 'Move to Support at Home' },
    );
    if (!ok) return;
    try {
      const r = await api.post(`/aged-care/chsp/clients/${form.id}/transition`, { sahStart: move.sahStart });
      setMove((p) => ({ ...p, result: r.data })); toast.success(`CHSP ends ${fmtDate(r.data.endDate)}`); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not move them'); }
  };
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [c, s] = await Promise.all([api.get('/aged-care/chsp/clients'), api.get('/aged-care/chsp/services')]);
      setRows(c.data?.clients || []);
      setServices((s.data?.services || []).filter((x) => x.is_chsp));
    } catch (e) { setError(e.response?.data?.message || 'Could not load CHSP participants'); setRows([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const today = isoDate();   // the local date, not UTC's
  const startNew = async () => {
    try { const r = await api.get('/aged-care/chsp/candidates'); setCandidates(Array.isArray(r.data) ? r.data : []); } catch { setCandidates([]); }
    setForm({ mode: 'new', clientId: '', startDate: today, serviceTypeIds: [], feeReductionPct: 0, feeReductionReason: '', notes: '', referralCode: '',
      privacyNoticeOn: today, privacyNoticeMethod: 'in_person', privacyNoticeVersion: 'CHSP Standard Privacy Notification' });
  };
  // A move typed for one participant must not carry over to the next one opened.
  const startEdit = (r) => { setMove(null); setForm({
    mode: 'edit', id: r.id, name: r.name, startDate: r.start_date, endDate: r.end_date || '', exitReason: r.exit_reason || '',
    serviceTypeIds: (r.service_type_ids || []).map(String), feeReductionPct: Number(r.fee_reduction_pct) || 0,
    feeReductionReason: r.fee_reduction_reason || '', notes: r.notes || '', hasSah: r.has_sah_plan, referralCode: r.referral_code || '', servicesInPlaceAt: r.services_in_place_at ? String(r.services_in_place_at).slice(0, 10) : '',
    // A changed reduction is a new decision from this date (today unless changed).
    feeReductionFrom: today, wasPct: Number(r.fee_reduction_pct) || 0, wasReason: r.fee_reduction_reason || '',
    feeReductionReviewDate: r.fee_review_date || '', policyId: r.policy_id || '',
    decisions: Array.isArray(r.decisions) ? r.decisions : [],
  }); };
  const toggleService = (id) => setForm((p) => ({ ...p, serviceTypeIds: p.serviceTypeIds.includes(id) ? p.serviceTypeIds.filter((x) => x !== id) : [...p.serviceTypeIds, id] }));
  const save = async () => {
    if (form.mode === 'new' && !form.clientId) { toast.error('Choose the participant'); return; }
    if (Number(form.feeReductionPct) > 0 && !form.feeReductionReason.trim()) { toast.error('Say why the contribution is reduced'); return; }
    if (form.endDate && !form.exitReason) { toast.error('Choose why CHSP services ended'); return; }
    setSaving(true);
    const body = {
      startDate: form.startDate, serviceTypeIds: form.serviceTypeIds, feeReductionPct: Number(form.feeReductionPct) || 0,
      feeReductionReason: form.feeReductionReason || null, notes: form.notes || null, referralCode: form.referralCode || null, servicesInPlaceAt: form.servicesInPlaceAt || null,
      feeReductionReviewDate: Number(form.feeReductionPct) > 0 ? (form.feeReductionReviewDate || null) : null,
      ...(form.mode === 'edit' ? { policyId: form.policyId || null } : {}),
    };
    try {
      if (form.mode === 'new') {
        await api.post('/aged-care/chsp/clients', { ...body, clientId: form.clientId, privacyNoticeOn: form.privacyNoticeOn || null,
          privacyNoticeMethod: form.privacyNoticeMethod, privacyNoticeVersion: form.privacyNoticeVersion });
      }
      else await api.patch(`/aged-care/chsp/clients/${form.id}`, { ...body, feeReductionFrom: form.feeReductionFrom || null, endDate: form.endDate || null, exitReason: form.endDate ? form.exitReason : null });
      toast.success(form.mode === 'new' ? 'Enrolled in CHSP' : 'Saved');
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
    finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => `${r.name} ${r.mac_id || ''}`.toLowerCase().includes(q)) : rows;
  }, [rows, search]);
  const active = rows.filter((r) => r.active);
  const columns = [
    {
      key: 'name', header: 'Participant', sortable: true,
      render: (r) => (
        <span className="min-w-0 flex items-center gap-2.5">
          <PersonAvatar id={r.client_id} name={r.name} size="sm" />
          <span className="min-w-0 block">
            <span className="font-medium text-slate-900 block truncate">{r.name}</span>
            {/* DEX will not take a CHSP client without one. */}
            <span className={clsx('text-[12px]', r.mac_id ? 'text-slate-500' : 'text-amber-700')}>{r.mac_id ? `My Aged Care ${r.mac_masked || r.mac_id}` : 'No My Aged Care ID: DEX needs it'}</span>
            {r.active && !r.privacy_notice_on && <span className="block text-[12px] text-amber-700">No CHSP privacy notification recorded</span>}
          </span>
        </span>
      ),
    },
    {
      key: 'start_date', header: 'Enrolled', width: 170, sortable: true,
      render: (r) => (
        <span className="block text-[13px]">
          <span className="tabular-nums text-slate-700">{fmtDate(r.start_date)}</span>
          {r.end_date && <span className="block text-[12px] text-slate-500">to {fmtDate(r.end_date)}{r.exit_reason ? `, ${r.exit_reason}` : ''}</span>}
        </span>
      ),
    },
    {
      key: 'services', header: 'Referred for', width: 220,
      render: (r) => (r.service_names?.length
        ? <span className="text-slate-700 text-[13px] line-clamp-2">{r.service_names.join(', ')}</span>
        : <span className="text-slate-500">Any CHSP service</span>),
    },
    {
      key: 'fee_reduction_pct', header: 'Contribution', width: 140, sortable: true,
      render: (r) => (Number(r.fee_reduction_pct) >= 100 ? <span className="text-teal-800 font-medium">Waived</span>
        : Number(r.fee_reduction_pct) > 0 ? <span className="text-teal-800">{num(r.fee_reduction_pct)}% reduced</span>
          : <span className="text-slate-600">Full</span>),
    },
    {
      key: 'year', header: 'This year', width: 170, align: 'right', sortValue: (r) => r.year?.visits || 0, sortable: true,
      render: (r) => (
        <span className="block text-right text-[13px]">
          <span className="tabular-nums text-slate-800">{r.year?.visits || 0} visit{r.year?.visits === 1 ? '' : 's'}</span>
          <span className="block text-[12px] text-slate-500 tabular-nums">{num(r.year?.hours || 0)} h · {money(r.year?.fees || 0)}</span>
        </span>
      ),
    },
    { key: 'active', header: 'Status', width: 100, render: (r) => <span className={clsx('badge', r.active ? 'badge-green' : 'badge-gray')}>{r.active ? 'Active' : 'Ended'}</span> },
  ];

  return (
    <div>
      <FigureStrip>
        <Figure label="Enrolled now" value={active.length} icon={Users} />
        <Figure label="Without a My Aged Care ID" value={active.filter((r) => !r.mac_id).length} tone={active.some((r) => !r.mac_id) ? 'amber' : 'slate'} sub={active.some((r) => !r.mac_id) ? 'DEX will not accept them' : 'All can be reported'} />
        <Figure label="Contribution reduced or waived" value={active.filter((r) => Number(r.fee_reduction_pct) > 0).length} />
        <Figure label="Also on Support at Home" value={active.filter((r) => r.has_sah_plan).length} tone={active.some((r) => r.has_sah_plan) ? 'amber' : 'slate'} sub={active.some((r) => r.has_sah_plan) ? 'End CHSP when Support at Home starts' : null} />
      </FigureStrip>
      <Register
        title="CHSP participants" count={filtered.length} countLabel="enrolled" icon={Users}
        search={search} onSearch={setSearch} searchPlaceholder="Search by name or My Aged Care ID"
        actions={<button type="button" onClick={startNew} className="btn-primary btn-sm"><UserPlus size={14} /> Enrol participant</button>}
        columns={columns} rows={filtered} rowKey="id" loading={loading} error={error} onRetry={load}
        onRowClick={startEdit} rowTone={(r) => (r.active && (!r.mac_id || r.has_sah_plan) ? 'amber' : undefined)}
        empty={{
          icon: Users, title: 'No one is enrolled in CHSP yet',
          hint: 'Enrol an aged care participant to book CHSP services for them. Add them in Participants first if they are new.',
          action: <button type="button" onClick={startNew} className="btn-primary btn-sm"><UserPlus size={14} /> Enrol participant</button>,
        }}
        footer="While enrolled, a participant's visits are CHSP visits: their contribution is invoiced to them, nothing is drawn from a Support at Home budget, and the visits go in the monthly DEX file."
      />

      {form && (
        <DetailDrawer open onClose={() => setForm(null)} icon={HeartHandshake} width="md"
          title={form.mode === 'new' ? 'Enrol in CHSP' : form.name} subtitle={form.mode === 'new' ? 'An aged care participant receiving CHSP services' : 'CHSP enrolment'}
          footer={(
            <>
              <button type="button" onClick={() => setForm(null)} className="btn-secondary">Cancel</button>
              <button type="button" onClick={save} disabled={saving} className="btn-primary"><Save size={15} /> {saving ? 'Saving…' : form.mode === 'new' ? 'Enrol' : 'Save'}</button>
            </>
          )}>
          <div className="space-y-4">
            {form.mode === 'new' && (
              <Field label="Participant" hint={candidates.length ? null : 'Everyone in aged care is already enrolled, or there are no aged care participants yet. Add one in Participants.'}>
                {(id) => (
                  <select id={id} className="input-field" value={form.clientId} onChange={(e) => setForm((p) => ({ ...p, clientId: e.target.value }))}>
                    <option value="">Choose…</option>
                    {candidates.map((c) => <option key={c.id} value={c.id}>{c.name}{c.has_sah_plan ? ' (has a Support at Home plan)' : ''}</option>)}
                  </select>
                )}
              </Field>
            )}
            {form.hasSah && (
              <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" /> They also have a Support at Home plan. While this enrolment runs their visits are billed as CHSP. End it on the day Support at Home starts.
              </p>
            )}
            <FormRow cols={2}>
              <Field label="CHSP services start">
                {(id) => <input id={id} type="date" className="input-field" value={form.startDate || ''} onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))} />}
              </Field>
              {form.mode === 'edit' && (
                <Field label="CHSP services end" hint="Leave empty while they are still receiving CHSP.">
                  {(id) => <input id={id} type="date" className="input-field" value={form.endDate || ''} onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))} />}
                </Field>
              )}
            </FormRow>
            {form.mode === 'edit' && form.endDate && (
              <Field label="Why they stopped">
                {(id) => (
                  <select id={id} className="input-field" value={form.exitReason} onChange={(e) => setForm((p) => ({ ...p, exitReason: e.target.value }))}>
                    <option value="">Choose…</option>
                    {EXIT_REASONS.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                )}
              </Field>
            )}
            <fieldset>
              <legend className="text-[13px] font-medium text-slate-700 mb-1">Referred for</legend>
              <p className="text-[12px] text-slate-500 mb-2">The services in their My Aged Care referral. Leave all unticked to allow any CHSP service.</p>
              {services.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {services.map((s) => (
                    <label key={s.service_type_id} className="flex items-center gap-2 text-[13px] text-slate-800">
                      <input type="checkbox" checked={form.serviceTypeIds.includes(String(s.service_type_id))} onChange={() => toggleService(String(s.service_type_id))} />
                      <span className="truncate">{s.name}</span>
                    </label>
                  ))}
                </div>
              ) : <p className="text-[12px] text-amber-700">No CHSP services are set up yet. Set them up under Services and targets.</p>}
            </fieldset>
            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <p className="text-[13px] font-medium text-slate-800">Contribution</p>
              <Field label="Reduction" hint="Under your hardship policy: 0 pays the full contribution, 100 waives it.">
                {(id) => (
                  <div className="flex items-center gap-2">
                    <input id={id} type="number" min="0" max="100" step="5" className="input-field !w-28" value={form.feeReductionPct} onChange={(e) => setForm((p) => ({ ...p, feeReductionPct: e.target.value }))} />
                    <span className="text-[13px] text-slate-500">%</span>
                  </div>
                )}
              </Field>
              {Number(form.feeReductionPct) > 0 && (
                <Field label="Review by" hint="When the hardship decision is looked at again. You are reminded two weeks before.">
                  {(id) => <input id={id} type="date" className="input-field !w-auto" value={form.feeReductionReviewDate || ''} onChange={(e) => setForm((p) => ({ ...p, feeReductionReviewDate: e.target.value }))} />}
                </Field>
              )}
              {Number(form.feeReductionPct) > 0 && (
                <Field label="Why" hint="Keep a record of each hardship decision.">
                  {(id) => <textarea id={id} rows={2} className="input-field" value={form.feeReductionReason} onChange={(e) => setForm((p) => ({ ...p, feeReductionReason: e.target.value }))} placeholder="For example, financial hardship assessed on 1 October" />}
                </Field>
              )}
              {form.mode === 'edit' && (Number(form.feeReductionPct) !== form.wasPct || form.feeReductionReason !== form.wasReason) && (
                <Field label="From" hint="Visits from this date are billed at the new contribution. Earlier visits keep the decision that applied then.">
                  {(id) => <input id={id} type="date" className="input-field !w-auto" value={form.feeReductionFrom || ''} min={form.startDate || undefined} onChange={(e) => setForm((p) => ({ ...p, feeReductionFrom: e.target.value }))} />}
                </Field>
              )}
              {form.mode === 'edit' && form.decisions?.length > 0 && (
                <div>
                  <p className="text-[12px] font-medium text-slate-600 mb-1">Decisions</p>
                  <ul className="space-y-1 text-[12px] text-slate-600">
                    {form.decisions.map((d, i) => (
                      <li key={`${d.from}-${i}`} className="flex flex-wrap gap-x-2">
                        <span className="tabular-nums">{fmtDate(d.from)}{d.to ? ` to ${fmtDate(d.to)}` : ' onwards'}</span>
                        <span className="font-medium text-slate-800">{Number(d.pct) >= 100 ? 'Waived' : Number(d.pct) > 0 ? `${num(d.pct)}% reduced` : 'Full contribution'}</span>
                        {d.reason && <span className="text-slate-500">{d.reason}</span>}
                        {d.review && <span className="text-slate-500">review by {fmtDate(d.review)}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {form.mode === 'new' && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                <p className="text-[13px] font-medium text-slate-800">CHSP privacy notification</p>
                <p className="text-[12px] text-slate-500">Given before their My Aged Care ID is used. It is a notice, not a consent: their consents to DEX storing their name, and to research contact, are recorded on the DEX screen.</p>
                <FormRow cols={2}>
                  <Field label="Given on">{(id) => <input id={id} type="date" className="input-field" value={form.privacyNoticeOn || ''} max={today} onChange={(e) => setForm((p) => ({ ...p, privacyNoticeOn: e.target.value }))} />}</Field>
                  <Field label="How">
                    {(id) => (
                      <select id={id} className="input-field" value={form.privacyNoticeMethod} onChange={(e) => setForm((p) => ({ ...p, privacyNoticeMethod: e.target.value }))}>
                        <option value="in_person">In person</option><option value="phone">Phone</option><option value="email">Email</option><option value="post">Post</option><option value="form">Signed form</option>
                      </select>
                    )}
                  </Field>
                </FormRow>
              </div>
            )}
            {form.mode === 'edit' && policies.length > 0 && (
              <Field label="Contribution policy" hint="The version this enrolment is under.">
                {(id) => (
                  <select id={id} className="input-field" value={form.policyId || ''} onChange={(e) => setForm((p) => ({ ...p, policyId: e.target.value }))}>
                    <option value="">Not recorded</option>
                    {policies.map((p) => <option key={p.id} value={p.id}>Version {p.version} (from {fmtDate(p.effective_from)})</option>)}
                  </select>
                )}
              </Field>
            )}
            {form.mode === 'edit' && policiesError && (
              <p className="text-[12px] text-red-800" role="alert">
                {policiesError} <button type="button" className="underline ml-1" onClick={loadPolicies}>Try again</button>
              </p>
            )}
            {form.mode === 'edit' && form.hasSah && !form.endDate && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2">
                <p className="text-[13px] font-medium text-slate-800">Move to Support at Home</p>
                <p className="text-[12px] text-slate-600">CHSP ends the day before Support at Home starts: the enrolment ends, the DEX case closes with the exit reason, and their later visits are checked under Support at Home.</p>
                <div className="flex flex-wrap items-end gap-2">
                  <Field label="Support at Home starts">{(id) => <input id={id} type="date" className="input-field !w-auto" value={move?.sahStart || ''} onChange={(e) => setMove({ sahStart: e.target.value })} />}</Field>
                  <button type="button" className="btn-primary btn-sm mb-0.5" disabled={!move?.sahStart} onClick={transition}>Move</button>
                </div>
                {move?.result && (
                  <div className="text-[12px] text-slate-700 space-y-0.5">
                    <p>CHSP ended {fmtDate(move.result.endDate)}{move.result.exitReason ? `; DEX exit reason: ${move.result.exitReason.desc}` : '; choose the DEX exit reason on the DEX screen'}.</p>
                    {move.result.visits.length > 0 && <p>{move.result.visits.length} visit{move.result.visits.length === 1 ? '' : 's'} from then on{move.result.visits.some((v) => !v.sah_mapped) ? ', some on services not set up for Support at Home' : ''}.</p>}
                    {move.result.billed.length > 0 && <p className="text-amber-800">Billed as CHSP after the move: {move.result.billed.map((b) => b.invoice_number || b.id).join(', ')}. Credit and bill again under Support at Home.</p>}
                  </div>
                )}
              </div>
            )}
            <Field label="My Aged Care referral code" hint="CHSP visits are held until the participant has a My Aged Care ID or this code.">
              {(id) => <input id={id} className="input-field" value={form.referralCode || ''} onChange={(e) => setForm((p) => ({ ...p, referralCode: e.target.value }))} />}
            </Field>
            <Field label="Services in place recorded in My Aged Care" hint="After the first service, on their client record in the My Aged Care provider portal.">
              {(id) => <input id={id} type="date" className="input-field" value={form.servicesInPlaceAt || ''} onChange={(e) => setForm((p) => ({ ...p, servicesInPlaceAt: e.target.value }))} />}
            </Field>
            <Field label="Notes">
              {(id) => <textarea id={id} rows={2} className="input-field" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />}
            </Field>
            {form.mode === 'edit' && (
              <p className="flex items-start gap-2 text-[12px] text-slate-500">
                <FileBarChart2 size={14} className="mt-0.5 shrink-0" /> Their DEX details (country of birth, language, living arrangements and the rest) are recorded on the DEX screen.
              </p>
            )}
          </div>
        </DetailDrawer>
      )}
    </div>
  );
}
